import { useCallback } from 'react';
import MapBox, { Layer, type LayerProps, type MapRef, Source } from 'react-map-gl/mapbox';
import { MapStyle } from '~/common/types';
import getConfig from '~/config';
import type { LayerPreviewStyle } from './layerAssets';
import { PREVIEW_BOUNDS, PREVIEW_SOURCE_ID, previewFeatureCollection, previewLayerSpecs } from './layerPreview';

const config = getConfig();

/**
 * Live-map preview rendered inside the layer info popover.
 *
 * Each popover open creates its OWN mapbox instance, destroyed on close — deliberately
 * NOT `reuseMaps`: react-map-gl's reuse pool is global and unkeyed (unmounting maps
 * stack onto one shared pool; the next `reuseMaps` mount pops whichever is on top), and
 * the main `components2/Map` recycles through it — sharing the pool let the preview and
 * the main map steal each other's instances (wrong camera/style/canvas size on both).
 *
 * The map must be RENDERED inside the popover content — never DOM-reparented into it.
 * Radix classifies pointer/focus events as inside/outside via the React tree (capture
 * handlers on the content), so a DOM-moved map reads as "outside" and instantly
 * dismisses the popover (its canvas is focusable, and open-autofocus lands on it).
 */
export function LayerPreview({ style }: { style: LayerPreviewStyle }) {
  // Ref callback (the Map.tsx idiom): runs on attach. The popover content animates
  // open, so guard the canvas size and re-pin the hardcoded viewport once attached.
  const mapRef = useCallback((map: MapRef | null) => {
    if (map) {
      map.resize();
      map.fitBounds(PREVIEW_BOUNDS, { animate: false });
    }
  }, []);

  return (
    // Control-free thumbnail: the default attribution "(i)" control is off and none is added.
    <div className="size-full" data-testid="layer-preview">
      <MapBox
        ref={mapRef}
        id="layerLibraryPreview"
        mapStyle={MapStyle.light}
        mapboxAccessToken={config.mapboxToken}
        initialViewState={{ bounds: PREVIEW_BOUNDS }}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        scrollZoom={false}
        dragPan={false}
        dragRotate={false}
        doubleClickZoom={false}
        touchZoomRotate={false}
        keyboard={false}
      >
        <Source id={PREVIEW_SOURCE_ID} type="geojson" data={previewFeatureCollection(style.geometry)}>
          {previewLayerSpecs(style).map((spec) => (
            <Layer key={spec.id} {...(spec as LayerProps)} />
          ))}
        </Source>
      </MapBox>
    </div>
  );
}
